import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, getRecentArticles } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'night-life');
  const [articles, setArticles] = useState<Article[]>([]);
  const [currentCategory, setCurrentCategory] = useState(categories[0]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    const cat = categories.find((c) => c.slug === activeSlug) || categories[0];
    setCurrentCategory(cat);
    setArticles(getArticlesByCategory(activeSlug));
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeSlug]);

  const allArticles = getRecentArticles(6);

  const pageSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${currentCategory.name} - 夜无疆`,
      description: currentCategory.description,
      url: `https://wwyjs.cn/category/${activeSlug}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: articles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Article',
          name: article.title,
          url: `https://wwyjs.cn/article/${article.id}`,
        },
      })),
    },
  ];

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 夜无疆`}
        description={currentCategory.description}
        keywords={`${currentCategory.name},夜无疆,夜间探索,${currentCategory.name}内容`}
        canonicalUrl={`/category/${activeSlug}`}
        schema={pageSchema}
      />
      <main className="pt-20">
        {/* Category Header */}
        <section className="relative py-16 md:py-20 bg-background-900">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500/15">
                    <i className={`${currentCategory.icon} text-primary-400 text-xl`}></i>
                  </span>
                  <h1 className="font-heading text-3xl md:text-4xl text-foreground-100">{currentCategory.name}</h1>
                </div>
                <p className="text-foreground-500 text-base max-w-xl">{currentCategory.description}</p>
              </div>
              <div className="text-foreground-600 text-sm">
                <strong className="text-primary-400">{articles.length}</strong> 篇文章
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="bg-background-950 border-b border-background-800/50 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-950'
                      : 'bg-background-800 text-foreground-500 hover:text-foreground-300 hover:bg-background-700'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="py-12 md:py-16 bg-background-950">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2">
                {articles.length > 0 ? (
                  <div className="space-y-6">
                    {articles.map((article) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="group flex flex-col sm:flex-row gap-5 p-5 rounded-xl bg-background-900 border border-background-800/50 hover:border-primary-500/20 transition-all duration-300 cursor-pointer"
                      >
                        <div className="sm:w-48 h-36 sm:h-auto flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 text-xs text-foreground-600 mb-2">
                            <span>{article.publishDate}</span>
                            <span>{article.readTime}</span>
                          </div>
                          <h3 className="font-heading text-lg text-foreground-100 group-hover:text-primary-400 transition-colors duration-200 mb-2">
                            {article.title}
                          </h3>
                          <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-3">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-2">
                            <img
                              src={article.authorAvatar}
                              alt={article.author}
                              className="w-6 h-6 rounded-full object-cover"
                              loading="lazy"
                            />
                            <span className="text-xs text-foreground-600">{article.author}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-800">
                      <i className="ri-inbox-line text-foreground-500 text-2xl"></i>
                    </div>
                    <p className="text-foreground-500">该分类暂无文章</p>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <div className="bg-background-900 rounded-xl border border-background-800/50 p-5">
                    <h3 className="font-heading text-lg text-foreground-200 mb-4 flex items-center gap-2">
                      <i className="ri-time-line text-primary-400"></i>
                      最新文章
                    </h3>
                    <div className="space-y-4">
                      {allArticles.slice(0, 5).map((article) => (
                        <Link
                          key={article.id}
                          to={`/article/${article.id}`}
                          className="flex gap-3 group cursor-pointer"
                        >
                          <div className="w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden bg-background-800">
                            <img
                              src={article.imageUrl}
                              alt={article.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-foreground-600 mb-1">{article.publishDate}</p>
                            <p className="text-sm text-foreground-400 group-hover:text-primary-400 transition-colors duration-200 line-clamp-2">
                              {article.title}
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}